import { redirect, type MiddlewareFunction } from 'react-router';

import { queryClient } from '@app/providers/query';

import { accountQueryOptions, isAdmin } from '@entities/shared/account';
import { clientsQueryOptions, useSelectedClientStore } from '@entities/shared/client';

import { accessTokenService, clientIdService } from '@shared/lib/storage';

// Bootstrap gate for the whole private tree. As RR v8 middleware it runs to
// completion BEFORE any child loader in the subtree — unlike a parent loader,
// which RR runs in parallel with child loaders. That ordering is the whole
// point: the selected client (and thus the X-Client-Id header) is resolved here
// first, so downstream permission/data requests never fire without a client
// scope. Not calling `next()` is intentional — RR runs the child handlers
// automatically once this resolves.
export const privateMiddleware: MiddlewareFunction = async () => {
  if (!accessTokenService.get()) throw redirect('/login');

  const account = await queryClient.ensureQueryData(accountQueryOptions());

  // Admins operate without a client scope — no X-Client-Id, no clients request.
  if (isAdmin(account)) {
    useSelectedClientStore.getState().setClientId(null);
    return;
  }

  const clients = await queryClient.ensureQueryData(clientsQueryOptions());

  const savedClientId = clientIdService.get();
  const isSavedClientValid = clients.some((client) => client.clientId === savedClientId);
  const fallbackClientId = clients[0]?.clientId ?? null;

  const nextClientId = isSavedClientValid ? savedClientId : fallbackClientId;

  useSelectedClientStore.getState().setClientId(nextClientId);
};
